// DrillInBar — the bar over a library item the author has gone INTO (library
// drill-in 2026-10-01): a quiet "12 / 18 ‹ ›" walking the list exactly as it
// was shown, and an x closing the sheet back to the table (D34). Each arrow is labelled with the
// neighbour's title, so it says where it goes. Pure: the host maps each click
// to its one intent (close to the list / step to a neighbour). Entered from
// somewhere else (the Studio), `back` leads there first.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Text } from "../Text/Text.js";
import type { SearchResult } from "./searchModel.js";

// Where the opened item sits in the shown list, and its neighbours.
export type ListPosition = {
    readonly index: number;
    readonly total: number;
    readonly previous?: SearchResult;
    readonly next?: SearchResult;
};

// The opened item's place in `order`, or undefined when it is not in it (a
// deep link the current query filters out): then the bar shows no stepper.
export const positionIn = (
    order: readonly SearchResult[],
    id: string,
): ListPosition | undefined => {
    const index = order.findIndex((r) => r.id === id);
    if (index < 0) return undefined;
    const previous = order[index - 1];
    const next = order[index + 1];
    return {
        index,
        total: order.length,
        ...(previous !== undefined ? { previous } : {}),
        ...(next !== undefined ? { next } : {}),
    };
};

export const DrillInBar = ({
    note,
    back,
    position,
    onClose,
    onStep,
}: {
    // The way back to where the item was opened from ("Back to Studio").
    readonly back?: { readonly label: string; readonly onBack: () => void };
    // A quiet word at the left (the item is archived).
    readonly note?: string;
    readonly position: ListPosition | undefined;
    readonly onClose: () => void;
    readonly onStep: (to: SearchResult) => void;
}): ReactNode => {
    const { t } = useLingui();
    const step = (
        to: SearchResult | undefined,
        label: string,
        back: boolean,
    ) => (
        <IconButton
            label={to !== undefined ? `${label}: ${to.title}` : label}
            size="sm"
            disabled={to === undefined}
            onClick={() => to !== undefined && onStep(to)}
        >
            <Icon name={back ? "chevron-left" : "chevron-right"} size="sm" />
        </IconButton>
    );
    return (
        <nav className="bsx-bar" aria-label={t`Library`}>
            {back === undefined ? null : (
                <Button variant="ghost" size="sm" onClick={back.onBack}>
                    <Icon name="arrow-left" size="sm" />
                    {back.label}
                </Button>
            )}
            <Text as="span" size="xs" tone="dim" className="bsx-bar-note">
                {note}
            </Text>
            {position !== undefined && position.total > 1 ? (
                <div className="bsx-step">
                    <Text as="span" size="xs" tone="dim">
                        {`${position.index + 1} / ${position.total}`}
                    </Text>
                    {step(position.previous, t`Previous`, true)}
                    {step(position.next, t`Next`, false)}
                </div>
            ) : null}
            <IconButton
                label={t`Close`}
                title={t`Close`}
                size="sm"
                onClick={onClose}
            >
                <Icon name="close" size="sm" />
            </IconButton>
        </nav>
    );
};
